import React, { useContext } from 'react';
import { cn } from '@/lib/utils';
import { Icon, ShimmerBlock } from 'venn-ui-kit';
import { PortfolioTreeView } from 'venn-components';
import type { CombinePortfoliosDraftSettings } from '../types';
import { isNil } from 'lodash';
import type { Portfolio } from 'venn-api';
import { CombinePortfoliosWizardContext } from '../logic/CombinePortfoliosWizardContext';

const getDisplayDepth = (settings: CombinePortfoliosDraftSettings): number | undefined => {
  if (settings.structureOption === 'SEPARATE') {
    return undefined;
  }
  let depth = 1;
  if (settings.includeHousehold) {
    depth += 1;
  }
  if (settings.includeClient) {
    depth += 1;
  }
  return depth;
};

const getPortfolioKey = (portfolio: Portfolio) =>
  isNil(portfolio.id) ? `portfolio-name-${portfolio.name}` : `portfolio-id-${portfolio.id}`;

export const PortfolioPreview = () => {
  const context = useContext(CombinePortfoliosWizardContext);
  return (
    <div className={cn('w-full h-full flex flex-col py-[24px] px-[40px] bg-venn-grey-10')}>
      <div className={cn('text-[24px]')}>Preview</div>
      <div className={cn('border-0 border-b border-solid border-venn-grey my-[16px]')} />
      {isNil(context.combineSettings.structureOption) ? (
        <EmptyState />
      ) : context.portfoliosLoading ? (
        <ShimmerBlock />
      ) : (
        <PortfolioTree portfolios={context.portfolios} displayDepth={getDisplayDepth(context.combineSettings)} />
      )}
    </div>
  );
};

interface PortfolioTreeProps {
  portfolios: Portfolio[];
  displayDepth: number | undefined;
}

const PortfolioTree = React.memo(function PortfolioTreeInner({ portfolios, displayDepth }: PortfolioTreeProps) {
  return (
    <div className={cn('flex-1 basis-0 overflow-y-auto')}>
      {portfolios.map((portfolio) => (
        <div key={getPortfolioKey(portfolio)} className={cn('py-[4px]')}>
          <PortfolioTreeView portfolio={portfolio} depth={displayDepth} />
        </div>
      ))}
    </div>
  );
});

const EmptyState = () => (
  <div className={cn('flex flex-1 items-center justify-center')}>
    <Icon type="folders" className={cn('text-[187px] text-venn-grey-20')} />
  </div>
);
